import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useApiClient } from '@/utils/api/ApiClient'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { PaymentAgentDto } from 'api/Wallet/Types/PaymentAgentDto'
import { FlexBox } from 'components/Box'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getPaymentAgentModalFilterFields } from 'components/Filter/paymentAgentFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import paymentAgentColumn from './paymentAgentsColumn'

import './RecordsPaymentAgents.module.scss'

export type SelectPaymentAgentEvent = { filterId?: string; value: PaymentAgentDto[] }

interface SelectClientsProps {
  filterId?: string
  onSelect: (props: SelectPaymentAgentEvent) => void
  onClose: () => void
}

export const SelectPaymentAgentTable = (props: SelectClientsProps): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const { gridState } = useGridState()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [paymentAgents, setPaymentAgents] = useState<PaymentAgentDto[]>([])

  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedPaymentAgents, setSelectedPaymentAgents] = useState<PaymentAgentDto[]>([])

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterActionState: GridFilterActionState
        ): Promise<PageData<PaymentAgentDto[]>> => {
          const response = await walletsApiClient.getPaymentAgents({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          const paymentAgentFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value =
            paymentAgentFilter?.value?.filter((paymentAgent: PaymentAgentDto) => paymentAgent) || []

          setSelectedPaymentAgents(value)
          if (response?.items) {
            setPaymentAgents(
              response.items.map((paymentAgent: PaymentAgentDto) => {
                if (value.length <= 0) {
                  return { ...paymentAgent, selected: false }
                }

                const match = value?.find((x: PaymentAgentDto) => x.id === paymentAgent.id)
                const isSelected = !!match

                return { ...paymentAgent, selected: isSelected }
              })
            )
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<PaymentAgentDto[]>>
  ) => {
    setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults
      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await walletsApiClient.getPaymentAgents({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setPaymentAgents(response.items.map((x) => ({ ...x, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [getQuery, gridFilterState, gridState.pageSize, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const selectPaymentAgents = (paymentAgentItems: PaymentAgentDto[]) =>
    paymentAgentItems.map((paymentAgent: PaymentAgentDto) => {
      const match = selectedPaymentAgents.find((x) => x.id === paymentAgent.id)
      const isSelected = !!match
      return { ...paymentAgent, selected: isSelected }
    })

  const handleSubmitPaymentAgent = () => {
    props.onSelect({
      filterId: props.filterId,
      value: uniqBy(selectedPaymentAgents, 'id'),
    })
    props.onClose()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await walletsApiClient.getPaymentAgents({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setPaymentAgents(selectPaymentAgents(response.items))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setPaymentAgents(
      paymentAgents.map((paymentAgent) => ({ ...paymentAgent, selected: isChecked }))
    )

    const paymentAgentsSelected = isChecked
      ? uniqBy([...paymentAgents, ...selectedPaymentAgents], 'id')
      : selectedPaymentAgents.filter((selectedPaymentAgent) => {
          const hasPaymentAgent = paymentAgents.find((x) => x.id === selectedPaymentAgent.id)

          return !hasPaymentAgent
        })

    setSelectedPaymentAgents(paymentAgentsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const paymentAgent = paymentAgents.find((x) => x.id === event.dataItem.id)
      if (paymentAgent) {
        setSelectedPaymentAgents([...selectedPaymentAgents, paymentAgent])
      }
    } else {
      setSelectedPaymentAgents(selectedPaymentAgents.filter((x) => x.id !== event.dataItem.id))
    }

    setPaymentAgents(
      paymentAgents.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div className='records-paymentAgents' style={{ margin: 16 }}>
      <SelectFiltersFacade
        gridState={gridFilterState}
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={filterFetch}
        filterType={FilterType.PAYMENT_AGENT}
        filterFields={getPaymentAgentModalFilterFields}
        containerClassName='records-paymentAgents__filter'
        getQuery={getQuery}
        showFilterWhenInactive
      />
      <Grid
        {...gridFilterState}
        resizable
        data={paymentAgents}
        selectedField='selected'
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ maxHeight: '70vh' }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={paymentAgents.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        {paymentAgentColumn
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
      <FlexBox style={{ margin: 32 }} alignItems='center' justifyContent='center'>
        <Button style={{ cursor: 'pointer' }} onClick={handleSubmitPaymentAgent}>
          Save
        </Button>
      </FlexBox>
    </div>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}
